import { useState } from "react";
import { HeartHandshake, Coins, Building2, Scale, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "./SectionTitle";
import { DonationPayment } from "./DonationPayment";

const TYPES = [
  { value: "infaq", label: "Infaq", icon: HeartHandshake },
  { value: "sedekah", label: "Sedekah", icon: Coins },
  { value: "zakat", label: "Zakat", icon: Scale },
  { value: "pembangunan", label: "Dana Pembangunan", icon: Building2 },
];
const PRESETS = [50000, 100000, 250000, 500000];

export const DonationSection = ({ settings, full = false }) => {
  const [form, setForm] = useState({ type: "infaq", donor_name: "", anonymous: false, phone: "", amount: "", method: "" });
  const [proof, setProof] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.method) return toast.error("Pilih metode pembayaran terlebih dahulu");
    if (!proof) return toast.error("Unggah foto bukti transfer");
    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("proof", proof);
    try {
      await api.post("/donations", fd);
      setDone(true);
      toast.success("Donasi terkirim, menunggu konfirmasi pengurus");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <section id="donasi" className="scroll-mt-24" data-testid="donation-section">
        <div className="rounded-3xl emerald-bg pattern-overlay text-white p-10 text-center max-w-2xl mx-auto" data-testid="donation-success">
          <CheckCircle2 size={48} className="mx-auto text-[#d4af37]" />
          <h3 className="mt-4 text-2xl font-bold">Jazakallahu Khairan</h3>
          <p className="mt-2 text-emerald-100">Donasi Anda telah kami terima dan akan dikonfirmasi oleh pengurus. Semoga Allah membalas dengan pahala berlipat.</p>
          <Button variant="outline" className="mt-6 rounded-full text-emerald-950" onClick={() => { setDone(false); setForm({ type: "infaq", donor_name: "", anonymous: false, phone: "", amount: "", method: "" }); setProof(null); setShowUpload(false); }} data-testid="donation-again-btn">
            Donasi Lagi
          </Button>
        </div>
      </section>
    );

  return (
    <section id="donasi" className="scroll-mt-24" data-testid="donation-section">
      {!full && <SectionTitle eyebrow="Donasi Online" title="Salurkan Infaq & Sedekah Anda" desc="Bantu kemakmuran Masjid Al-Iman dari mana saja. Pilih jenis donasi, transfer ke rekening atau scan QRIS, lalu unggah bukti agar pengurus dapat mengonfirmasi dan nama Anda tampil di daftar donatur." more="/donasi" moreTestId="donation-see-all" />}
      <form onSubmit={submit} className={`${full ? "" : "mt-10"} grid lg:grid-cols-2 gap-6`}>
        <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 sm:p-8 space-y-6">
          <div>
            <Label>Jenis Donasi</Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {TYPES.map((t) => (
                <button key={t.value} type="button" onClick={() => set("type", t.value)} data-testid={`donation-type-${t.value}`}
                  className={`rounded-2xl border p-4 text-left transition-colors ${form.type === t.value ? "emerald-bg text-white border-transparent" : "border-emerald-900/10 hover:border-emerald-700/40"}`}>
                  <t.icon size={18} className={form.type === t.value ? "text-[#d4af37]" : "text-emerald-700"} />
                  <p className="mt-2 text-sm font-semibold">{t.label}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="donor_name">Nama Donatur</Label>
              <Input id="donor_name" value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)} disabled={form.anonymous} placeholder={form.anonymous ? "Hamba Allah" : "Nama lengkap"} data-testid="donation-name-input" />
              <label className="flex items-center gap-2 text-sm text-slate-600 pt-1">
                <Checkbox checked={form.anonymous} onCheckedChange={(v) => set("anonymous", !!v)} data-testid="donation-anonymous-checkbox" /> Donasi sebagai Anonim (Hamba Allah)
              </label>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">No. WhatsApp <span className="text-slate-400 font-normal">(opsional)</span></Label>
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="08xxxxxxxxxx" data-testid="donation-phone-input" />
              <p className="text-xs text-slate-400">Untuk menerima ucapan terima kasih via WA</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount">Nominal Donasi (Rp)</Label>
            <Input id="amount" type="number" min={1000} required value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="50000" data-testid="donation-amount-input" />
            <div className="flex flex-wrap gap-2 pt-1">
              {PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => set("amount", p)} className="text-xs rounded-full border border-emerald-900/15 px-3 py-1 hover:bg-emerald-50" data-testid={`donation-preset-${p}`}>
                  Rp{p.toLocaleString("id-ID")}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DonationPayment settings={settings} method={form.method} setMethod={(m) => set("method", m)} showUpload={showUpload} setShowUpload={setShowUpload} proof={proof} setProof={setProof}>
          {showUpload && (
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11 bg-[#d4af37] text-emerald-950 hover:bg-[#e2c15a]" data-testid="donation-submit-btn">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Kirim Donasi"}
            </Button>
          )}
        </DonationPayment>
      </form>
    </section>
  );
};
